import API from '../interceptors/Api';
import { queryClient } from '@/queryClient';

/**
 * The category list is expensive on large workspaces (Bodega Bouza: 4.673 categorias INFLOW, ~2,7 MB,
 * ~12 s) and every "add category" modal used to refetch it from scratch. So the list getters below go
 * through React Query's cache instead of calling axios directly:
 *
 * - a warm entry is served with no request while it is fresh (CATEGORIES_STALE_TIME);
 * - concurrent callers share ONE in-flight request (two modals opening together no longer fire twice);
 * - every consumer benefits, including the ~28 legacy useEffect call sites, without touching them;
 * - the mutations in this file invalidate the cache, so the next read reflects the change.
 *
 * The response is workspace-scoped (the interceptor sends X-Workspace-Id / X-Workspace-View-Id from the
 * axios defaults), so that scope is part of the cache key — otherwise switching workspace would serve the
 * previous workspace's categories.
 */
const CATEGORIES_STALE_TIME = 15 * 60 * 1000;

const CATEGORIES_GC_TIME = 60 * 60 * 1000;

export const CATEGORIES_QUERY_KEY = 'categories';

const scope = () => ({
  workspaceId: API.defaults.headers.common['X-Workspace-Id'] ?? null,
  workspaceViewId: API.defaults.headers.common['X-Workspace-View-Id'] ?? null,
});

export const categoriesQueryKey = (params: Record<string, any> = {}) => [CATEGORIES_QUERY_KEY, scope(), params];

/** Marks every cached category list stale, so the next read refetches. */
export const invalidateCategories = () => queryClient.invalidateQueries({ queryKey: [CATEGORIES_QUERY_KEY] });

const fetchCategories = (params: Record<string, any> = {}) => queryClient.fetchQuery({
  queryKey: categoriesQueryKey(params),
  queryFn: () => API.get('/categories', { params }),
  staleTime: CATEGORIES_STALE_TIME,
  gcTime: CATEGORIES_GC_TIME,
  // Hand out a copy: callers sort/splice their list in place, which would corrupt the shared entry.
}).then((data) => (Array.isArray(data) ? [...data] : data));

export const getCategories = (params?) => fetchCategories(params);

export const getCategoriesInflow = (params: Record<string, any> = {}) => fetchCategories({ type: 'INFLOW', ...params });

export const getCategoriesOutflow = (params: Record<string, any> = {}) => fetchCategories({ type: 'OUTFLOW', ...params });

/**
 * Warms both lists right after login, so the first "add category" modal opens against a filled cache.
 * Failures are swallowed on purpose: a workspace without the categories module answers 403, and a
 * background warm-up must never surface as an app error.
 */
export const prefetchCategories = () => Promise.all([
  getCategoriesInflow().catch(() => null),
  getCategoriesOutflow().catch(() => null),
]);

export const createCategory = async (data) => {
  const created = await API.post('/categories', data);
  invalidateCategories();
  return created;
};

export const getCategoryDetail = (id) => API.get(`/categories/${id}`);

export const editCategory = async (id, data) => {
  const updated = await API.put(`/categories/${id}`, data);
  invalidateCategories();
  return updated;
};

export const deleteCategory = async (id) => {
  const deleted = await API.delete(`/categories/${id}`);
  invalidateCategories();
  return deleted;
};

export const categoriesReorder = async (data) => {
  const reordered = await API.put('/categories/reorder', data);
  invalidateCategories();
  return reordered;
};

export const getCategoryRules = (params) => API.get('/category-rules', {
  params,
});

export const createCategoryRule = (data) => API.post('/category-rules', data);

export const getCategoryRule = (id) => API.get(`/category-rules/${id}`);

export const updateCategoryRule = (id, data) => API.put(`/category-rules/${id}`, data);

export const deleteCategoryRule = (id) => API.delete(`/category-rules/${id}`);
